import { ChevronRight, Pin, PinOff } from 'lucide-react'
import { type MouseEvent, type ReactNode, useState } from 'react'
import { NavLink, useLocation } from 'react-router-dom'

import { useCurrentUser, useHealth } from '../api/auth'
import { Icon } from '../lib/icons'
import { BrandMark } from './BrandLogo'

/** Orden de docs/plan_bot_trading.md §9.2: Dashboard primero (gráficos y
 *  análisis de la flota, la vista por defecto al entrar), después lo que
 *  alguien mira si un bot está haciendo algo raro con plata real (Bots,
 *  Alertas), y el resto detrás. Dashboard/Bots/Alertas van como ítems
 *  planos —sin agrupar bajo "Operación"— porque son las pantallas que más
 *  se visitan de entrada; agruparlas detrás de un acordeón les agrega un
 *  click sin ningún beneficio de organización, mismo criterio que ya
 *  valía para "Exchanges".
 *
 *  `subItems`, si existe, convierte el ítem en un acordeón (botón que
 *  expande/colapsa en vez de un link directo) en vez de una sección con
 *  título fijo: un `<h3>` que aparece/desaparece con el sidebar corre
 *  todo lo de abajo (mide alto por line-height, no por ancho, así que
 *  `max-width:0` no alcanza para que no ocupe lugar) — un acordeón, en
 *  cambio, es una fila más, del mismo alto colapsado o expandido, así que
 *  nada se corre al pasar el mouse; sólo un click explícito revela sus
 *  hijos. `to` de un ítem con `subItems` es sólo la clave de
 *  `openSubmenus`, nunca una ruta real —esa rama nunca renderiza un
 *  `NavLink`. */
type NavItem = {
  to: string
  label: string
  icon: string
  end?: boolean
  subItems?: NavItem[]
  /** Sub-rutas de `to` que NO deben prender este ítem — Historial cuelga de
   *  `/bots/historial` (URL-wise es "debajo" de Bots) pero tiene su propia
   *  fila en el nav, así que sin esto las dos quedarían activas a la vez
   *  al mirar el historial. */
  activeExcept?: string[]
}

const NAV_ITEMS: NavItem[] = [
  { to: '/dashboard', label: 'Dashboard', icon: 'dashboard' },
  {
    to: '/bots',
    label: 'Bots',
    icon: 'bots',
    activeExcept: ['/bots/historial', '/bots/transacciones', '/bots/calendario'],
  },
  { to: '/bots/historial', label: 'Historial', icon: 'history', end: true },
  { to: '/bots/transacciones', label: 'Transacciones', icon: 'transactions', end: true },
  { to: '/bots/calendario', label: 'Calendario', icon: 'calendar', end: true },
  { to: '/alertas', label: 'Alertas', icon: 'alerts' },
  { to: '/journal', label: 'Journal', icon: 'journal' },
  { to: '/reporte-fiscal', label: 'Reporte fiscal', icon: 'taxReport' },
  { to: '/exchanges', label: 'Exchanges', icon: 'exchanges' },
  {
    to: 'investigacion',
    label: 'Backtesting',
    icon: 'research',
    subItems: [
      { to: '/matriz', label: 'Matriz', icon: 'matrix', end: true },
      { to: '/runs', label: 'Runs', icon: 'runs' },
      { to: '/walk-forward', label: 'Walk-forward', icon: 'walkforward' },
      { to: '/datos', label: 'Datos', icon: 'data' },
      { to: '/estrategias', label: 'Estrategias', icon: 'strategies' },
    ],
  },
]

//: Sólo para administradores — sumado al final de NAV_ITEMS dentro del
//: componente (no acá: NAV_ITEMS es una constante de módulo, mutarla desde
//: dentro de un render pisaría entre renders/instancias). Ícono `operations`
//: (Activity de lucide) ya declarado en lib/icons.tsx pero sin usar por
//: ningún otro nav item.
const SYSTEM_NAV_ITEM: NavItem = {
  to: 'sistema',
  label: 'Sistema',
  icon: 'operations',
  subItems: [
    { to: '/sistema', label: 'Salud y registros', icon: 'operations', end: true },
    { to: '/sistema/usuarios', label: 'Usuarios', icon: 'users' },
    { to: '/sistema/papelera', label: 'Papelera', icon: 'trash' },
  ],
}

//: Aparte de `NAV_ITEMS` a propósito — vive en el footer del sidebar
//: (`app-sidebar__footer`, más abajo), no en la lista scrolleable de
//: secciones: es la única entrada que no es "una pantalla de trabajo", así
//: que se separa visualmente del resto en vez de ser una fila más.
const HELP_ITEM: NavItem = { to: '/ayuda', label: 'Ayuda', icon: 'help' }
const SETTINGS_ITEM: NavItem = { to: '/configuracion', label: 'Configuración', icon: 'settings' }

function StatusDot({ status }: { status: 'ok' | 'error' | undefined }) {
  const modifier = status === 'ok' ? 'status-dot--ok' : status === 'error' ? 'status-dot--error' : ''
  return <span className={`status-dot ${modifier}`} aria-hidden="true" />
}

function NavItemLink({
  item,
  onNavigate,
  openSubmenus,
  onToggleSubmenu,
}: {
  item: NavItem
  onNavigate: () => void
  openSubmenus: Record<string, boolean>
  onToggleSubmenu: (key: string) => void
}) {
  const { pathname } = useLocation()

  if (!item.subItems) {
    // No el `isActive` que calcula NavLink solo: con `activeExcept` de por
    // medio hace falta el propio pathname para poder descartar esas
    // sub-rutas, así que se computa acá en vez de confiar en el default.
    const isActive = item.end
      ? pathname === item.to
      : pathname === item.to ||
        (pathname.startsWith(`${item.to}/`) && !item.activeExcept?.includes(pathname))
    return (
      <NavLink
        to={item.to}
        end={item.end}
        onClick={onNavigate}
        className={() => `app-sidebar__link${isActive ? ' is-active' : ''}`}
      >
        <Icon name={item.icon} className="app-sidebar__link-icon" size={18} />
        <span className="app-sidebar__link-label">{item.label}</span>
      </NavLink>
    )
  }

  // El botón del acordeón queda "activo" si la ruta actual matchea
  // cualquiera de sus hijos, aunque esté cerrado — así se ve en qué
  // sección se está aunque el submenú no esté desplegado.
  const isOpen = Boolean(openSubmenus[item.to])
  const hasActiveChild = item.subItems.some((sub) => pathname === sub.to)

  return (
    <div className="app-sidebar__accordion">
      <button
        type="button"
        className={`app-sidebar__link app-sidebar__link--toggle${hasActiveChild ? ' is-active' : ''}`}
        onClick={() => onToggleSubmenu(item.to)}
        aria-expanded={isOpen}
      >
        <Icon name={item.icon} className="app-sidebar__link-icon" size={18} />
        <span className="app-sidebar__link-label">{item.label}</span>
        <ChevronRight
          className={`app-sidebar__accordion-chevron${isOpen ? ' is-open' : ''}`}
          size={14}
        />
      </button>
      <div className={`app-sidebar__submenu${isOpen ? ' is-open' : ''}`}>
        {item.subItems.map((sub) => (
          <NavLink
            key={sub.to}
            to={sub.to}
            end={sub.end}
            onClick={onNavigate}
            className={({ isActive }) =>
              `app-sidebar__link app-sidebar__link--sub${isActive ? ' is-active' : ''}`
            }
          >
            <Icon name={sub.icon} className="app-sidebar__link-icon" size={16} />
            <span className="app-sidebar__link-label">{sub.label}</span>
          </NavLink>
        ))}
      </div>
    </div>
  )
}

export function AppSidebar({
  brand,
  open,
  pinned,
  onNavigate,
  onTogglePin,
}: {
  brand: ReactNode
  /** Sólo importa en mobile (el cajón); en desktop el sidebar siempre está
   *  montado — se contrae a íconos en reposo y se expande con hover/foco,
   *  o queda expandido fijo con `pinned`. */
  open: boolean
  pinned: boolean
  /** Cierra el cajón al tocar un link — en desktop no hace nada visible. */
  onNavigate: () => void
  onTogglePin: () => void
}) {
  const { data: health } = useHealth()
  const { data: user } = useCurrentUser()
  const navItems = user?.is_admin ? [...NAV_ITEMS, SYSTEM_NAV_ITEM] : NAV_ITEMS
  const [openSubmenus, setOpenSubmenus] = useState<Record<string, boolean>>({})

  function toggleSubmenu(key: string) {
    setOpenSubmenus((current) => ({ ...current, [key]: !current[key] }))
  }

  // `openSubmenus` sobrevive al colapso del sidebar —el CSS sólo lo tapa
  // visualmente con max-height:0 mientras está colapsado, el estado de
  // React sigue en `true`—, así que sin esto un acordeón abierto una vez
  // reaparece abierto cada vez que se vuelve a pasar el mouse. Resetear al
  // salir el mouse hace que cada expansión arranque siempre comprimida.
  // Fijado no entra: ahí el sidebar no se comprime al sacar el mouse, así
  // que un acordeón abierto es una elección real, no un resto de la
  // última vez que se expandió.
  //
  // `(hover: hover)`, no sólo `pinned`: en mobile (touch) tocar "Backtesting"
  // dispara `click` (abre el acordeón) y, como el navegador no tiene un
  // mouse real que "se quede" sobre el sidebar, sintetiza un `mouseleave`
  // justo después para simular que el puntero se fue — sin este chequeo,
  // eso cerraba el acordeón en el mismo toque que lo abría, antes de que
  // llegara a pintarse abierto. Sólo un dispositivo con hover real (mouse)
  // tiene un "el mouse se fue" que sea cierto.
  function collapseSubmenusOnLeave() {
    if (!pinned && window.matchMedia('(hover: hover)').matches) setOpenSubmenus({})
  }

  // Un click deja foco en el link/botón tocado — y como :focus-within
  // también mantiene expandido el sidebar (para que tabular con teclado
  // funcione), ese foco que sobrevivía al click lo dejaba pegado abierto
  // hasta el próximo click en cualquier otro lado, aunque el mouse ya se
  // hubiera ido. `document.activeElement`, no `event.target`: si el click
  // cae sobre un ícono SVG adentro del link/botón (no focusable), el foco
  // real queda en el link/botón, no en el SVG — blurrear el target exacto
  // no le saca el foco a nada. Los acordeones, con ícono + texto + chevron,
  // tienen mucha más superficie de ícono que un link simple, así que este
  // caso se daba seguido. Sigue sin romper el caso de teclado: tabular no
  // pasa por acá (no hay click).
  // `event.detail` es 0 en un `click` disparado por teclado (Enter/Espacio
  // activando un botón enfocado) y >=1 en uno real de mouse/touch — sin este
  // chequeo, activar un acordeón con el teclado bubbleaba el click hasta acá
  // y le sacaba el foco al propio botón que se acababa de activar, cortando
  // la navegación por teclado en seco.
  function blurClicked(event: MouseEvent) {
    if (event.detail === 0) return
    if (document.activeElement instanceof HTMLElement) document.activeElement.blur()
  }

  return (
    <aside
      className={`app-sidebar${open ? ' app-sidebar--open' : ''}${pinned ? ' app-sidebar--pinned' : ''}`}
      onClick={blurClicked}
      onMouseLeave={collapseSubmenusOnLeave}
    >
      <div className="app-sidebar__brand">
        <BrandMark className="app-sidebar__mark" />
        <span className="app-sidebar__brand-label">{brand}</span>
      </div>

      <nav className="app-sidebar__nav">
        {navItems.map((item) => (
          <NavItemLink
            key={item.to}
            item={item}
            onNavigate={onNavigate}
            openSubmenus={openSubmenus}
            onToggleSubmenu={toggleSubmenu}
          />
        ))}
      </nav>

      <div className="app-sidebar__footer">
        <NavItemLink
          item={SETTINGS_ITEM}
          onNavigate={onNavigate}
          openSubmenus={openSubmenus}
          onToggleSubmenu={toggleSubmenu}
        />

        <NavItemLink
          item={HELP_ITEM}
          onNavigate={onNavigate}
          openSubmenus={openSubmenus}
          onToggleSubmenu={toggleSubmenu}
        />

        <button
          type="button"
          className="app-sidebar__pin desktop-only"
          onClick={onTogglePin}
          aria-pressed={pinned}
          aria-label={pinned ? 'Dejar de fijar el sidebar' : 'Fijar el sidebar expandido'}
        >
          {pinned ? <PinOff size={16} /> : <Pin size={16} />}
          <span className="app-sidebar__link-label">
            {pinned ? 'Sidebar fijo' : 'Fijar sidebar'}
          </span>
        </button>

        <div className="app-sidebar__status">
          <span title="Postgres">
            <StatusDot status={health?.database.status} />
            <span className="app-sidebar__status-label mono">postgres</span>
          </span>
          <span title="Redis">
            <StatusDot status={health?.redis.status} />
            <span className="app-sidebar__status-label mono">redis</span>
          </span>
        </div>
      </div>
    </aside>
  )
}
